import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react'
import {
    Col, Row, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter,
    Container, FormFeedback, Alert, FormText, InputGroupText, InputGroup
} from 'reactstrap'
import InstantPaymentModal from "../instantPaymentCreatePage/InstantPaymentModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import { processPayment } from "../../../../api/requests/payment-requests";
import useIdempotencyKey from "../../../../hooks/useIdempotencyKey";
import { describeFetchError } from "../../../../utils/apiErrors";
import {useNavigate, useSearchParams} from "react-router-dom";
import '../../../../assets/PaymentForms.css';
import { usePaymentSession } from './hooks/usePaymentSession';
import { useCurrencyAmount } from './hooks/useCurrencyAmount';
import { useBinInstallments } from './hooks/useBinInstallments';
import { usePaymentModals } from './hooks/usePaymentModals';
import PayerInfoSection from './components/PayerInfoSection';
import CardInputSection from './components/CardInputSection';
import CreditCardPreview from '../newInsantPaymentForm/components/CreditCardPreview';
import PaymentPageHeader from './components/PaymentPageHeader';
import PaymentSecurityAside from './components/PaymentSecurityAside';
import { getPaymentTypeMeta } from './paymentTypeMeta';
import { mustSelectAccountForCollection } from '../../../../constants/roles';
import InstallmentTable from '../newInsantPaymentForm/components/InstallmentTable';
import ContractModal from '../newInsantPaymentForm/components/ContractModal';
import TermsCheckbox from '../newInsantPaymentForm/components/TermsCheckbox';

const initialCardVisual = {
    number: '•••• •••• •••• ••••',
    owner: 'AD SOYAD',
    month: 'AA',
    year: 'YY',
    cvv: '•••',
    amount: '₺ 0,00',
    brand: '',
    type: '',
    bank: '',
    schema: ''
};

const normalize = (s) =>
    (s ?? "")
        .toString()
        .normalize('NFKD')
        .replace(/[\W_]+/g, '')
        .toUpperCase();

const toPct = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return v;
    const cleaned = v.toString().replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
};

const formatTRY = n =>
    '₺ ' + (isNaN(n) ? '0,00' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const getTotal = (amountStr, commissionPercent) => {
    const amount = Number(amountStr || 0);
    const divisor = (100 - Number(commissionPercent || 0)) / 100;
    return amount / (divisor || 1);
};

const getCardBackground = (schema) => {
    if (schema === 'VISA') {
        return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)';
    } else if (schema === 'MASTERCARD') {
        return 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 50%, #ffb74d 100%)';
    } else if (schema === 'TROY') {
        return 'linear-gradient(135deg, #e1f5fe 0%, #81d4fa 50%, #4fc3f7 100%)';
    } else if (schema === 'AMEX') {
        return 'linear-gradient(135deg, #f3e5f5 0%, #ce93d8 50%, #ba68c8 100%)';
    } else {
        return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)';
    }
};

const digitsOnly = (s) => (s || '').replace(/\D/g, '');

/**
 * PhoneInput her zaman "+90" öneki üretir — alan hiç doldurulmasa bile değer "+90"
 * olabilir. Bu yüzden "boş" kontrolü ham metne değil, ülke kodu ayıklanmış hane
 * sayısına bakar. Geçerli değilse null döner ki opsiyonel alan boş sayılsın.
 */
const localPhoneDigits = (v) => (v || '').replace(/\D/g, '').replace(/^90/, '');
const normalizePhone = (v) => {
    const d = localPhoneDigits(v);
    return d.length === 10 ? `+90${d}` : null;
};
const stripSpaces = (s) => (s || '').replace(/\s/g, '');

const PaymentForm = () => {
    const navigate = useNavigate();
    const {
        currentUser, companyId, accounts, profile, finalRates, pageLoading, isAccountRole, isAccountAccountant
    } = usePaymentSession();
    const actionWord = isAccountRole ? 'Ödeme' : 'Tahsilat';

    const [searchParams] = useSearchParams();
    const urlType = searchParams.get('type');

    const isStaffAdmin = Array.isArray(currentUser?.roles)
        ? currentUser.roles.includes('ROLE_SUPER_ADMIN')
        : (typeof currentUser?.role === 'string' && currentUser.role === 'ROLE_SUPER_ADMIN');

    // Başlık/alt başlık/ikon ve 1. adım etiketi tek kaynaktan gelir; PayerInfoSection
    // da aynı meta'yı kullanır.
    const typeMeta = getPaymentTypeMeta(urlType, { isAccountRole, isStaffAdmin });

    // Plasiyer yalnız cari seçerek tahsilat yapar: anlık ve fatura bilgisiyle tahsilat ona kapalı.
    const accountRequired = mustSelectAccountForCollection(currentUser?.roles || []);
    useEffect(() => {
        if (accountRequired && urlType !== 'account') navigate('/payment?type=account', { replace: true });
    }, [accountRequired, urlType, navigate]);

    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const effectiveCompanyId = isStaffAdmin && selectedCompanyId ? selectedCompanyId : companyId;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    /*
     * Kart işlemi geri alınamaz: çift tıklama iki ayrı çekim demek. Anahtar form
     * açıldığında bir kez üretilir ve sayfa bu formda kaldığı sürece sabit kalır.
     * Başarıda sıfırlanmasına gerek yok — 3DS yanıtı belgeyi tamamen değiştiriyor,
     * kullanıcı bu formu bir daha görmüyor.
     */
    const paymentIdempotency = useIdempotencyKey();
    const [selectedInstallment, setSelectedInstallment] = useState({ count: 1, commission: 0 });
    const [cardVisual, setCardVisual] = useState(initialCardVisual);
    const [isFlipped, setIsCardFlipped] = useState(false);

    const [hasReadContract, setHasReadContract] = useState(false);

    document.title = `${actionWord} | Tahsilat360`;

    const gotoCreateInstantLinkPage = () => {
          if (!companyId) return;
           navigate(`/payment-links/create/${companyId}`);
         };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            useInvoiceFields: null,
            accountId: '',
            invoiceCompanyName: '',
            invoiceTaxNumber: "",
            invoiceTcNumber: "",
            invoiceTaxOffice: '',
            invoiceAddress: '',
            invoiceAccountType: '',
            userPhone: '',
            userAddress: '',
            email: '',
            amount: '',
            cc_owner: '',
            card_number: '',
            expiry_month: '',
            expiry_year: '',
            cvv: '',
            installment_count: '1'
        },
        validationSchema: Yup.object({
            accountId: Yup.string(),
            invoiceCompanyName: Yup.string()
                .when(['accountId', 'useInvoiceFields'], {
                    // 'useInvoiceFields' yerine 'useInvoiceFields === true'
                    is: (accountId, useInvoiceFields) => !accountId && useInvoiceFields === true,
                    then: schema => schema.required('Firma ünvanı zorunlu')
                }),
            invoiceTcNumber: Yup.string().when(["invoiceAccountType", "accountId", "useInvoiceFields"], {
                is: (type, acc, useInvoiceFields) => type === "SOLE_PROPRIETORSHIP" && !acc && useInvoiceFields === true,
                then: schema => schema.matches(/^\d{11}$/, "TC Kimlik 11 haneli olmalı").required("TC Kimlik zorunlu"),
                otherwise: schema => schema.strip()
            }),
            userAddress: Yup.string(),
            invoiceTaxNumber: Yup.string().when(["invoiceAccountType", "accountId", "useInvoiceFields"], {
                is: (type, acc, useInvoiceFields) => type !== "SOLE_PROPRIETORSHIP" && !acc && useInvoiceFields === true,
                then: schema => schema.matches(/^\d{10}$/, "Vergi No 10 haneli olmalı").required("Vergi No zorunlu"),
                otherwise: schema => schema.strip()
            }),
            email: Yup.string()
                .when(['accountId', 'useInvoiceFields'], {
                    is: (accountId, useInvoiceFields) => !accountId && useInvoiceFields === true,
                    then: schema => schema.email('Geçerli bir email adresi giriniz').required('Email adresi zorunlu')
                }),
            userPhone: Yup.string()
                .when(['accountId', 'useInvoiceFields'], {
                    is: (accountId, useInvoiceFields) => !accountId && useInvoiceFields === true,
                    then: schema => schema.matches(/^[0-9\s\-\+\(\)]+$/, 'Geçerli bir telefon numarası giriniz').min(10, 'Telefon numarası en az 10 haneli olmalı').required('Telefon numarası zorunlu'),
                    // Anlık ve Cari/Bayi ekranlarında alan opsiyoneldir: boş geçilebilir,
                    // ama yarım bırakılamaz — girilmişse tam 10 hane olmalıdır.
                    otherwise: schema => schema.test(
                        'optional-phone-10',
                        'Telefon numarası 10 haneli olmalı',
                        (v) => {
                            const d = localPhoneDigits(v);
                            return d.length === 0 || d.length === 10;
                        }
                    )
                }),
            invoiceTaxOffice: Yup.string()
                .when(['accountId', 'useInvoiceFields'], {
                    is: (accountId, useInvoiceFields) => !accountId && useInvoiceFields === true,
                    then: schema => schema.required('Vergi Dairesi zorunlu')
                }),
            invoiceAddress: Yup.string()
                .when(['accountId', 'useInvoiceFields'], {
                    is: (accountId, useInvoiceFields) => !accountId && useInvoiceFields === true,
                    then: schema => schema.required('Adres zorunlu')
                }),
            invoiceAccountType: Yup.string()
                .when(['accountId', 'useInvoiceFields'], {
                    is: (accountId, useInvoiceFields) => !accountId && useInvoiceFields === true,
                    then: schema => schema.required('Şirket tipi zorunlu')
                }),
            amount: Yup.number().required("Lütfen tutarı giriniz").min(1, "Tutar 1 TL'den az olamaz").max(99999999999, "Tutar çok yüksek"),
            cc_owner: Yup.string().required("Kart üzerindeki isim gereklidir"),
            card_number: Yup.string().required("Kart numarası gereklidir").min(13, "Kart numarası en az 13 haneli olmalıdır"),
            expiry_month: Yup.string().required("Ay seçiniz"),
            expiry_year: Yup.string().required("Yıl seçiniz"),
            cvv: Yup.string().required("CVV gereklidir").min(3, "CVV en az 3 haneli olmalıdır")
        }),
        onSubmit: async (values) => {
            const baseAmount = parseFloat(values.amount);
            const divisor = (100 - toPct(selectedInstallment.commission)) / 100;
            const totalAmount = baseAmount / divisor;

            if (!binInstallments.detectedCardBrand) {
                setErrorMessage('Kart markası tespit edilmedi. Lütfen sayfayı yenileyin.');
                return;
            }
            if (!binInstallments.cariGroups[binInstallments.selectedCariGroup]?.id) {
                setErrorMessage('Geçerli bir cari/bayi grup seçilmedi. Lütfen sayfayı yenileyin.');
                return;
            }

            const currentGroup = binInstallments.cariGroups[binInstallments.selectedCariGroup];
            const detBrand  = normalize(binInstallments.detectedCardBrand);
            const detSchema = normalize(cardVisual.schema);

            let validRate = currentGroup.installmentRates?.find(r => {
                const rateBrand = normalize(r.cardBrand);
                const countOk   = Number(r.installmentCount) === Number(selectedInstallment.count);
                const brandOk   = rateBrand === detBrand || rateBrand === detSchema;
                return r.enabled && countOk && brandOk;
            });

            if (!validRate) {
                validRate = currentGroup.installmentRates?.find(r =>
                    r.enabled && Number(r.installmentCount) === Number(selectedInstallment.count)
                );
            }

            const left  = toPct(validRate?.commissionPercent);
            const right = toPct(selectedInstallment?.commission);
            const diffOk = Math.abs(Number(left.toFixed(3)) - Number(right.toFixed(3))) <= 0.03;

            if (!validRate || !diffOk) {
                setErrorMessage('Taksit oranı tutarsız. Lütfen sayfayı yenileyin.');
                return;
            }

            const expectedTotal = getTotal(values.amount, toPct(selectedInstallment.commission));
            const totalsOk = Math.abs(Number(expectedTotal.toFixed(2)) - Number(totalAmount.toFixed(2))) <= 0.05;
            if (!totalsOk) {
                setErrorMessage('Tutar hesaplama hatası. Lütfen sayfayı yenileyin.');
                return;
            }

            setIsSubmitting(true);

            try {
                const useProfileData = !values.accountId && !values.useInvoiceFields;

                const body = {
                    companyId       : effectiveCompanyId,
                    accountId       : values.accountId || null,
                    invoiceAccountType: values.invoiceAccountType,
                    invoiceCompanyName: values.invoiceCompanyName || null,
                    invoiceTaxNumber: values.invoiceTaxNumber || null,
                    invoiceTcNumber: values.invoiceTcNumber || null,
                    invoiceTaxOffice: values.invoiceTaxOffice || null,
                    invoiceAddress: values.invoiceAddress || null,
                    accountGroupId  : binInstallments.cariGroups[binInstallments.selectedCariGroup]?.id,
                    baseAmount      : parseFloat(baseAmount.toFixed(2)),
                    calculatedAmount: parseFloat(totalAmount.toFixed(2)),
                    userName        : useProfileData ? profile.userName : (values.invoiceCompanyName || null),
                    email           : useProfileData ? profile.email : (values.email || null),
                    userAddress     : values.userAddress || profile.userAddress,
                    // Girilen opsiyonel numara önceliklidir; boş bırakılırsa eski davranış
                    // korunur (anlık tahsilatta oturum sahibinin profil telefonu).
                    userPhone       : normalizePhone(values.userPhone)
                                      || (useProfileData ? profile.userPhone : null),
                    installmentRate : selectedInstallment.count,
                    comissionRate   : toPct(selectedInstallment.commission),
                    cardNumber      : values.card_number.replace(/\s/g,''),
                    cardType        : cardVisual.brand || '',
                    expiryMonth     : values.expiry_month,
                    expiryYear      : values.expiry_year,
                    ccOwner         : values.cc_owner,
                    cvv             : values.cvv,
                    contractText    : modals.contractText || ''
                };

                const resp = await processPayment(body, paymentIdempotency.keyRef.current);

                const cType = resp.headers.get('content-type') || '';
                let htmlToRender = null;

                if (cType.includes('application/json')) {
                    const json = await resp.json();
                    if (resp.ok && json.statusCode === 200) {
                        htmlToRender = json.data;
                        setErrorMessage('');
                    } else {
                        // Sunucu isteği görüp reddetti: kart işlemi BAŞLAMADI. Kullanıcı
                        // tutarı/taksiti düzeltip tekrar gönderdiğinde gövde değişeceği
                        // için eski anahtar 422 üretirdi; bu yüzden tazeleniyor.
                        // 409/503'te tazelenmez (bkz. apiErrors.js) — orada istek
                        // sürüyor ya da tekrar denenmesi gereken aynı istek.
                        const detail = describeFetchError(resp, json, 'Ödeme işlemi başarısız oldu');
                        if (detail.shouldResetKey) paymentIdempotency.reset();
                        setErrorMessage(detail.message);
                    }
                } else {
                    htmlToRender = await resp.text();
                    setErrorMessage('');
                }

                if (htmlToRender) {
                    document.open('text/html', 'replace');
                    document.write(htmlToRender);
                    document.close();
                }
            } catch (err) {
                console.error('Ödeme hatası:', err);
                setErrorMessage('Ödeme sırasında teknik bir hata oluştu. Lütfen tekrar deneyiniz.');
            } finally {
                setIsSubmitting(false);
            }
        }
    });

    const { displayAmount, selectedCurrency, handleAmountChange, handleCurrencyChange } = useCurrencyAmount(
        finalRates,
        (tlAmount) => {
            validation.setFieldValue('amount', tlAmount);
            const currentCommission = selectedInstallment ? toPct(selectedInstallment.commission) : 0;
            const newTotal = getTotal(tlAmount, currentCommission);
            updateCardVisual('amount', formatTRY(newTotal));

            if (binInstallments.cariGroups.length && binInstallments.detectedCardBrand) {
                const { newInstallment } = binInstallments.renderInstallmentTable(
                    binInstallments.cariGroups[binInstallments.selectedCariGroup].installmentRates,
                    parseFloat(tlAmount || 0),
                    selectedInstallment || { count: 1, commission: 0 }
                );
                setSelectedInstallment(newInstallment || { count: 1, commission: 0 });
            }
        }
    );

    const amountPrefilled = useRef(false);
    useEffect(() => {
        const preset = Number(searchParams.get('amount'));
        if (amountPrefilled.current || !(preset > 0)) return;
        amountPrefilled.current = true;
        handleAmountChange({ target: { value: preset.toFixed(2).replace('.', ',') } });
    }, [searchParams, handleAmountChange]);

    const updateCardVisual = useCallback((field, value) => {
        setCardVisual(prev => ({ ...prev, [field]: value }));
    }, []);

    const binInstallments = useBinInstallments(
        companyId,
        validation,
        updateCardVisual,
        getTotal,
        formatTRY
    );

    const { filteredGroups, indexMap } = useMemo(() => {
        const groups = binInstallments.cariGroups || [];
        const out = [];
        const map = [];

        for (let i = 0; i < groups.length; i++) {
            const g = groups[i];
            const gCompanyId = g.companyId || g.company?.id || null;
            if (isStaffAdmin || !effectiveCompanyId || !gCompanyId || gCompanyId === effectiveCompanyId) {
                out.push(g);
                map.push(i);
            }
        }
        return { filteredGroups: out, indexMap: map };
    }, [binInstallments.cariGroups, effectiveCompanyId])

    useEffect(() => {
        if (!filteredGroups.length) return;

        const originalSel = binInstallments.selectedCariGroup;
        const stillVisible = indexMap.includes(originalSel);

        if (!stillVisible) {
            const firstOriginalIndex = indexMap[0];
            const syntheticEvent = { target: { value: String(firstOriginalIndex) } };
            binInstallments.handleCariGroupChange(syntheticEvent, validation.values.amount);
        }
    }, [filteredGroups, indexMap, binInstallments, validation.values.amount]);

    const modals = usePaymentModals(companyId, validation, profile, accounts);

    useEffect(() => {
        if (modals.modalErrorMessage) setErrorMessage(modals.modalErrorMessage);
    }, [modals.modalErrorMessage]);

    const currentInstallmentOptions = useMemo(() => {
        if (!binInstallments.cariGroups || binInstallments.cariGroups.length === 0 || binInstallments.selectedCariGroup == null) {
            return [];
        }
        const currentGroup = binInstallments.cariGroups[binInstallments.selectedCariGroup];
        const allRates = currentGroup ? (currentGroup.installmentRates || []) : [];
        const detBrand  = normalize(binInstallments.detectedCardBrand);
        const detSchema = normalize(cardVisual.schema);
        let brandSpecificRates = [];
        if (detBrand || detSchema) {
            brandSpecificRates = allRates.filter(r => {
                if (!r.enabled) return false;
                const rateBrand = normalize(r.cardBrand);
                return rateBrand === detBrand || rateBrand === detSchema;
            });
        }
        const ratesToUse = brandSpecificRates.length > 0
            ? brandSpecificRates
            : allRates.filter(r => r.enabled);
        const uniqueRatesMap = new Map();
        ratesToUse.forEach(rate => {
            if (rate && rate.installmentCount != null && !uniqueRatesMap.has(rate.installmentCount)) {
                uniqueRatesMap.set(rate.installmentCount, rate);
            }
        });

        const uniqueRates = Array.from(uniqueRatesMap.values());
        uniqueRates.sort((a, b) => a.installmentCount - b.installmentCount);
        return uniqueRates;
    }, [
        binInstallments.cariGroups,
        binInstallments.selectedCariGroup,
        binInstallments.detectedCardBrand,
        cardVisual.schema
    ]);

    const isInvoiceValid = useMemo(() => {
        if (!validation.values.useInvoiceFields || !!validation.values.accountId || isAccountRole) return true;
        return true;
    }, [validation.values, isAccountRole]);

    const isCardValid = useMemo(() => {
        const number = stripSpaces(validation.values.card_number);
        const cvv = digitsOnly(validation.values.cvv);
        const amt = Number(validation.values.amount);
        const currentGroup = binInstallments.cariGroups[binInstallments.selectedCariGroup];
        const hasCariGroup = !!currentGroup;

        return (
            !!validation.values.cc_owner &&
            number.length >= 13 &&
            !!validation.values.expiry_month &&
            !!validation.values.expiry_year &&
            cvv.length >= 3 &&
            !isNaN(amt) && amt >= 1 &&
            !!binInstallments.detectedCardBrand &&
            hasCariGroup
        );
    }, [validation.values, binInstallments.detectedCardBrand, binInstallments.cariGroups, binInstallments.selectedCariGroup]);

    const hasAccountOrValidInvoice = useMemo(() => {
        if (accountRequired) return !!validation.values.accountId;
        return !!validation.values.accountId || isInvoiceValid;
    }, [validation.values.accountId, isInvoiceValid, accountRequired]);

    const canProceedBase = useMemo(() => {
        return isCardValid && hasAccountOrValidInvoice;
    }, [isCardValid, hasAccountOrValidInvoice]);

    const canSubmit = useMemo(() => {
        return canProceedBase && modals.contractAccepted && binInstallments.binQueryStatus !== 'paytr_not_found' && !isSubmitting;
    }, [canProceedBase, modals.contractAccepted, binInstallments.binQueryStatus, isSubmitting]);

    const clearCardValidationState = () => {
        const cardFields = ['cc_owner', 'card_number', 'expiry_month', 'expiry_year', 'cvv', 'amount'];
        cardFields.forEach(field => {
            validation.setFieldTouched(field, false);
            validation.setFieldError(field, undefined);
        });
    };

    const handleCardNumberChange = async (e) => {
        const formatted = CardInputSection.formatCardNumber(e.target.value);
        validation.setFieldValue('card_number', formatted);

        const val = formatted.replace(/\s/g, '');
        let displayVal = '•••• •••• •••• ••••';
        if (val.length > 0) {
            displayVal = val.length <= 4 ? val :
                val.length >= 8 ? val.substr(0, 4) + ' •••• •••• ' + val.substr(val.length - 4, 4) :
                    val.substr(0, 4) + ' ' + '•'.repeat(val.length - 4);
        }
        updateCardVisual('number', displayVal);

        if (val.length >= 8) {
            const bin = val.substring(0, 8);
            if (bin !== binInstallments.lastBin) {
                binInstallments.setLastBin(bin);
                const { newInstallment } = await binInstallments.queryBIN(bin, validation.values.amount);
                setSelectedInstallment(newInstallment || { count: 1, commission: 0 });
            }
        } else {
            if (binInstallments.lastBin !== null) binInstallments.setLastBin(null);
            binInstallments.clearBinQueryResults();
        }
    };

    const handleOwnerChange = (e) => {
        const val = e.target.value.toUpperCase();
        validation.setFieldValue('cc_owner', val);
        updateCardVisual('owner', val || 'AD SOYAD');
    };

    const handleMonthChange = (e) => {
        validation.setFieldValue('expiry_month', e.target.value);
        updateCardVisual('month', e.target.value || 'AA');
    };

    const handleYearChange = (e) => {
        validation.setFieldValue('expiry_year', e.target.value);
        updateCardVisual('year', e.target.value || 'YY');
    };

    const handleCVVChange = (e) => {
        validation.setFieldValue('cvv', e.target.value);
        updateCardVisual('cvv', e.target.value ? e.target.value.replace(/./g, '•') : '•••');
    };

    /** Yeni: sözleşmeyi açma handler’ı (TermsCheckbox’tan çağrılır) */
    const handleOpenContract = async (e) => {
        e?.preventDefault?.();
        // Form alanları eksikse önce hataları göster
        if (!canProceedBase) {
            validation.setTouched(
                Object.keys(validation.values).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
                true
            );
            return;
        }
        setHasReadContract(false); // her açılışta sıfırla
        await modals.handleOpenContract(true); // hook mevcut entegrasyonu yapsın (metni getir + modalı aç)
    };

    if (pageLoading) {
        return <Container fluid className="mt-4"><p>Yükleniyor...</p></Container>;
    }

    return (
        <Container fluid className="mt-4 pay-page">
            <PaymentPageHeader
                meta={typeMeta}
                actionWord={actionWord}
                onCreateLink={gotoCreateInstantLinkPage}
                createLinkDisabled={!companyId}
            />

            <Row>
                <Col lg={8}>
                    <form onSubmit={validation.handleSubmit}>
                        {/* 1. adım — yalnız cari/firma veya fatura tipinde vardır;
                            anlık tahsilatta ödeyen bilgisi toplanmaz. */}
                        {typeMeta.stepLabel && (
                            <div className="pay-card">
                                <div className="pay-step-title">
                                    <span className="pay-step-badge">1</span>
                                    {typeMeta.stepLabel}
                                </div>
                                <PayerInfoSection
                                    validation={validation}
                                    isAccountRole={isAccountRole}
                                    isAccountAccountant={isAccountAccountant}
                                    accounts={accounts}
                                    clearCardValidationState={clearCardValidationState}
                                    isStaffAdmin={isStaffAdmin}
                                    selectedCompanyId={selectedCompanyId}
                                    setSelectedCompanyId={setSelectedCompanyId}
                                    hideHeading
                                />
                            </div>
                        )}

                        <div className="pay-card">
                            <div className="pay-step-title">
                                <span className="pay-step-badge">{typeMeta.stepLabel ? '2' : '1'}</span>
                                Ödeme Bilgileri
                            </div>

                            {/* Anlık tahsilatta ayrı bir "bilgiler" adımı yok, ama
                                PayerInfoSection yine render edilir: mod sıfırlama
                                efektleri ve rol bazlı davranışlar oradadır. Görünür
                                tek çıktısı opsiyonel telefon alanıdır ve bu kartın
                                içinde durur. */}
                            {!typeMeta.stepLabel && (
                                <PayerInfoSection
                                    validation={validation}
                                    isAccountRole={isAccountRole}
                                    accounts={accounts}
                                    clearCardValidationState={clearCardValidationState}
                                    isStaffAdmin={isStaffAdmin}
                                    selectedCompanyId={selectedCompanyId}
                                    setSelectedCompanyId={setSelectedCompanyId}
                                    hideHeading
                                />
                            )}

                                <FormGroup className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                                        <Label for="amount" className="mb-0">Çekilecek Tutar</Label>
                                        <small className="text-muted">
                                            <strong>USD:</strong> {finalRates.usdFinalRate ? `${parseFloat(finalRates.usdFinalRate).toFixed(2)} TL` : '-'} |{' '}
                                            <strong> EUR:</strong> {finalRates.eurFinalRate ? `${parseFloat(finalRates.eurFinalRate).toFixed(2)} TL` : '-'} |{' '}
                                            <strong> GBP:</strong> {finalRates.gbpFinalRate ? `${parseFloat(finalRates.gbpFinalRate).toFixed(2)} TL` : '-'}
                                        </small>
                                    </div>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            className="form-control form-control-lg"
                                            placeholder="0,00"
                                            value={displayAmount}
                                            onChange={handleAmountChange}
                                            onKeyDown={(e) => {
                                                const allowedControlKeys = [
                                                    'Backspace',
                                                    'Tab',
                                                    'ArrowLeft',
                                                    'ArrowRight',
                                                    'Delete',
                                                    'Home',
                                                    'End'
                                                ];
                                                if (e.ctrlKey || e.metaKey) {
                                                    return;
                                                }
                                                if (allowedControlKeys.includes(e.key)) {
                                                    return;
                                                }
                                                if (e.key === ',' || e.key === '.') {
                                                    return;
                                                }
                                                if (!/^[0-9]$/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            invalid={validation.touched.amount && validation.errors.amount}
                                        />
                                        <InputGroupText>
                                            <Input
                                                type="select"
                                                value={selectedCurrency}
                                                onChange={handleCurrencyChange}
                                                style={{border: 'none', background: 'transparent'}}
                                            >
                                                <option value="TL">TL</option>
                                                <option value="USD">USD</option>
                                                <option value="EUR">EUR</option>
                                                <option value="GBP">GBP</option>
                                            </Input>
                                        </InputGroupText>
                                    </InputGroup>
                                    <FormFeedback>{validation.errors.amount}</FormFeedback>
                                    <FormText color="info">
                                        <i className="bx bx-info-circle me-1"></i>
                                        Para birimini yandaki tl seçeneğine tıklayıp değiştirebilirsiniz.
                                    </FormText>
                                </FormGroup>

                                <CardInputSection
                                    validation={validation}
                                    onCardNumberChange={handleCardNumberChange}
                                    onOwnerChange={handleOwnerChange}
                                    onMonthChange={handleMonthChange}
                                    onYearChange={handleYearChange}
                                    onCvvChange={handleCVVChange}
                                    onCvvFocus={() => setIsCardFlipped(true)}
                                    onCvvBlur={() => setIsCardFlipped(false)}
                                    detectedCardBrand={binInstallments.detectedCardBrand}
                                />

                                {binInstallments.showCariGroup && (
                                    <FormGroup className="mb-3">
                                        <Label className="fw-bold mb-2">Taksit Grubu Seçiniz:</Label>
                                        <Input
                                              type="select"
                                              value={
                                                    (() => {
                                                          const fIndex = indexMap.indexOf(binInstallments.selectedCariGroup);
                                                          return fIndex === -1 ? '' : String(fIndex);
                                                        })()
                                              }
                                              onChange={(e) => {

                                                    const filteredIndex = Number(e.target.value);
                                                const originalIndex = indexMap[filteredIndex];
                                                const syntheticEvent = { target: { value: String(originalIndex) } };

                                                    const result = binInstallments.handleCariGroupChange(syntheticEvent, validation.values.amount);
                                                const grpIndex = result.selectedCariGroup ?? originalIndex;
                                                const grp = binInstallments.cariGroups[grpIndex];
                                                const fallback = grp?.installmentRates?.find(r => r.enabled) || { installmentCount: 1, commissionPercent: 0 };
                                                const ni = result.newInstallment || { count: fallback.installmentCount ?? 1, commission: fallback.commissionPercent ?? 0 };
                                                setSelectedInstallment({ count: Number(ni.count ?? 1), commission: toPct(ni.commission ?? 0) });
                                              }}
                                            >
                                            {filteredGroups.map((group, filteredIdx) => (
                                                <option key={indexMap[filteredIdx]} value={filteredIdx}>
                                                      {group.groupName}
                                                    </option>
                                              ))}
                                              {!filteredGroups.length && (
                                                <option value="">— Uygun grup bulunamadı —</option>
                                              )}
                                            </Input>
                                    </FormGroup>
                                )}

                                {binInstallments.binQueryStatus === 'success' && (
                                    <div className="mb-3"
                                         style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '5px', padding: '10px' }}
                                    >
                                        <InstallmentTable
                                            options={currentInstallmentOptions}
                                            selectedCount={selectedInstallment.count}
                                            amount={parseFloat(validation.values.amount || 0)}
                                            onSelect={(count, commissionPercent) => {
                                                setSelectedInstallment({ count: Number(count), commission: toPct(commissionPercent) });
                                                validation.setFieldValue('installment_count', String(count));
                                                updateCardVisual('amount', formatTRY(getTotal(validation.values.amount, toPct(commissionPercent))));
                                            }}
                                        />
                                    </div>
                                )}

                                {validation.values.amount && (
                                    <Alert color="info" className="mb-3">
                                        <strong>Ödenecek Toplam Tutar: </strong>
                                        {formatTRY(getTotal(validation.values.amount, toPct(selectedInstallment?.commission ?? 0)))}
                                    </Alert>
                                )}

                                {/* Yeni: Harici TermsCheckbox */}
                                <TermsCheckbox
                                    checked={modals.contractAccepted}
                                    canCheck={canProceedBase}
                                    hasReadContract={hasReadContract}
                                    onChange={modals.toggleContractAccepted}
                                    onOpenContract={handleOpenContract}
                                />

                                <div className="d-grid payment-mobile-spacer mb-4 mb-md-0">
                                    <Button type="submit" color="warning" size="lg" disabled={!canSubmit}>
                                        {isSubmitting ? 'İşleniyor...' : 'ÖDEMEYİ TAMAMLA'}
                                    </Button>
                                </div>

                                {!canSubmit && validation.values.amount > 0 && binInstallments.binQueryStatus === 'success' && (
                                    <FormText color="danger" className="mt-2 text-center">
                                        {!canProceedBase && "Lütfen bilgileri eksiksiz doldurun."}
                                        {canProceedBase && !modals.contractAccepted && "Lütfen ödeme sözleşmesini onaylayın."}
                                    </FormText>
                                )}

                                {errorMessage && <Alert color="danger" className="mt-2">{errorMessage}</Alert>}

                                {binInstallments.binQueryStatus === 'loading' && (
                                    <div className="text-info mt-1"><small>Kart bilgileri kontrol ediliyor...</small></div>
                                )}
                                {binInstallments.binQueryStatus === 'success' && (
                                    <div className="text-success mt-1"><small>Kart tanımlandı: {cardVisual.bank} - {cardVisual.brand}</small></div>
                                )}
                                {binInstallments.binQueryStatus === 'paytr_not_found' && (
                                    <div className="text-danger mt-1"><small>Bu kart için taksit seçeneği bulunamadı.</small></div>
                                )}
                                {binInstallments.binQueryStatus === 'error' && (
                                    <div className="text-danger mt-1"><small>Kart kontrol edilirken bir hata oluştu.</small></div>
                                )}
                        </div>
                    </form>

                    <div className="pay-footnote">
                        <i className="bx bx-info-circle" />
                        Ödeme işlemi 3D Secure güvenlik doğrulaması ile tamamlanır.
                    </div>
                </Col>

                <Col lg={4}>
                    <CreditCardPreview
                        cardVisual={cardVisual}
                        isFlipped={isFlipped}
                        getCardBackground={() => getCardBackground(cardVisual.schema)}
                        hideSecurityNote
                    />
                    <PaymentSecurityAside />
                </Col>
            </Row>

            {/* Yeni: Harici ContractModal kullanımı */}
            <ContractModal
                open={modals.showContract}
                onClose={modals.handleCloseContract}
                onAccept={modals.handleAcceptContract}
                contractText={modals.contractText}
                onFullyRead={() => setHasReadContract(true)}
            />

            {/* Diğer mevcut modallar */}
            <InstantPaymentModal
                isOpen={modals.instantModalOpen}
                toggle={modals.handleInstantModalToggle}
                companyId={companyId}
                onSuccess={modals.handleLinkCreationSuccess}
            />

            <Modal isOpen={modals.showCloseConfirmation} toggle={modals.cancelCloseModal} centered>
                <ModalHeader toggle={modals.cancelCloseModal}>Modal Kapatma Onayı</ModalHeader>
                <ModalBody>
                    <div className="text-center">
                        <i className="bx bx-question-mark display-4 text-warning mb-3"></i>
                        <h5>Emin misiniz?</h5>
                        <p className="text-muted">Girdiğiniz veriler kaybolacaktır.</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={modals.cancelCloseModal}>Hayır, Devam Et</Button>
                    <Button color="danger" onClick={modals.confirmCloseModal}>Evet, Kapat</Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default PaymentForm;